/**
 * Sub-abas de fluxo assistencial+faturamento do módulo Convênios · Consultas.
 * Elegibilidade · Autorizações · Guias TISS · Lotes · Glosas · Relatórios.
 *
 * V1 manual — sem integração TISS/API. Só cadastro interno.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Plus, RefreshCw, Trash2, CheckCircle2, XCircle, Send, ChevronsRight, FileText, DollarSign, AlertTriangle, BarChart3 } from "lucide-react";

// ------- helpers
const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const cls = "text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest font-bold";
const ELIG_CLS = { pending: "bg-amber-100 text-amber-800", eligible: "bg-emerald-100 text-emerald-800", ineligible: "bg-red-100 text-red-800" };
const AUTH_CLS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-800", expired: "bg-slate-200 text-slate-700" };
const CLAIM_CLS = { draft: "bg-slate-100 text-slate-700", submitted: "bg-blue-100 text-blue-800", under_review: "bg-amber-100 text-amber-800", paid: "bg-emerald-100 text-emerald-800", denied: "bg-red-100 text-red-800", appealed: "bg-purple-100 text-purple-800" };
const BATCH_CLS = { open: "bg-slate-100 text-slate-700", sent: "bg-blue-100 text-blue-800", under_review: "bg-amber-100 text-amber-800", closed: "bg-emerald-100 text-emerald-800" };

// ========================================== ELIGIBILITY
const EligibilityTab = ({ insurers }) => {
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    const params = statusFilter ? { status: statusFilter } : {};
    const { data } = await api.get("/admin/convenios/eligibility", { params });
    setList(data);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ patient_name: "", insurer_id: insurers?.[0]?.id || "", beneficiary_number: "", result: "pending" });
    setModal(true);
  };

  const save = async () => {
    if (!form.patient_name || !form.insurer_id || !form.beneficiary_number) {
      return toast.error("Preencha nome, operadora e nº beneficiário");
    }
    try {
      await api.post("/admin/convenios/eligibility", {
        professional_id: form.professional_id || "",
        patient_name: form.patient_name,
        insurer_id: form.insurer_id,
        plan_id: form.plan_id || null,
        beneficiary_number: form.beneficiary_number,
        result: form.result,
        notes: form.notes || null,
      });
      toast.success("Verificação criada");
      setModal(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const setResult = async (row, result) => {
    try {
      await api.patch(`/admin/convenios/eligibility/${row.id}`, { result });
      toast.success(`Marcado ${result}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const remove = async (row) => {
    if (!window.confirm("Excluir verificação?")) return;
    try { await api.delete(`/admin/convenios/eligibility/${row.id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex items-center gap-1 border border-slate-200 rounded-full p-1 bg-white">
          {[["", "Todos"], ["pending", "Pendentes"], ["eligible", "Elegíveis"], ["ineligible", "Não elegíveis"]].map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v)}
              data-testid={`elig-filter-${v || "todos"}`}
              className={`text-xs px-3 py-1 rounded-full ${statusFilter === v ? "bg-[#0B2B62] text-white" : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <button onClick={openNew} data-testid="elig-new"
          className="text-xs px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-[#0B2B62] font-semibold rounded-md inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Nova verificação
        </button>
      </div>
      <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-4 py-3">Paciente</th><th className="text-left px-4 py-3">Operadora</th><th className="text-left px-4 py-3">Carteirinha</th><th className="text-left px-4 py-3">Resultado</th><th className="text-right px-4 py-3">Ações</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500 text-xs">Nenhuma verificação.</td></tr>}
            {list.map(r => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3">{r.patient_name}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{insurers.find(i => i.id === r.insurer_id)?.name || "—"}</td>
                <td className="px-4 py-3 text-slate-600 text-xs font-mono">{r.beneficiary_number}</td>
                <td className="px-4 py-3"><span className={`${cls} ${ELIG_CLS[r.result]}`}>{r.result}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5 justify-end">
                    {r.result === "pending" && (<>
                      <button onClick={() => setResult(r, "eligible")} className="text-xs px-2.5 py-1 text-emerald-700 border border-emerald-300 rounded-md hover:bg-emerald-50 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Elegível</button>
                      <button onClick={() => setResult(r, "ineligible")} className="text-xs px-2.5 py-1 text-red-600 border border-red-200 rounded-md hover:bg-red-50 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />Não</button>
                    </>)}
                    <button onClick={() => remove(r)} className="text-xs px-2.5 py-1 text-red-600 border border-red-200 rounded-md hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div className="font-display font-bold text-lg text-[#0B2B62]">Nova verificação de elegibilidade</div>
              <button onClick={() => setModal(false)}><XCircle className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <input placeholder="Nome do paciente *" value={form.patient_name || ""} onChange={e => setForm(p => ({ ...p, patient_name: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              <select value={form.insurer_id || ""} onChange={e => setForm(p => ({ ...p, insurer_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">{insurers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}</select>
              <input placeholder="Nº da carteirinha *" value={form.beneficiary_number || ""} onChange={e => setForm(p => ({ ...p, beneficiary_number: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" />
              <input placeholder="ID do profissional (opcional)" value={form.professional_id || ""} onChange={e => setForm(p => ({ ...p, professional_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" />
              <select value={form.result || "pending"} onChange={e => setForm(p => ({ ...p, result: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"><option value="pending">Pendente</option><option value="eligible">Elegível</option><option value="ineligible">Não elegível</option></select>
              <textarea placeholder="Notas" rows={2} value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
              <button onClick={save} className="px-4 py-2 text-sm bg-[#0B2B62] text-white rounded-md font-semibold hover:bg-slate-800">Criar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================== AUTHORIZATIONS
const AuthorizationsTab = ({ insurers }) => {
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    const params = statusFilter ? { status: statusFilter } : {};
    const { data } = await api.get("/admin/convenios/authorizations", { params });
    setList(data);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (row, status) => {
    try {
      await api.patch(`/admin/convenios/authorizations/${row.id}`, { status });
      toast.success(status);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const remove = async (row) => {
    if (!window.confirm("Excluir autorização?")) return;
    try { await api.delete(`/admin/convenios/authorizations/${row.id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const save = async () => {
    if (!form.patient_name || !form.insurer_id) return toast.error("Preencha os campos");
    try {
      // Iter 68.64 — payload completo do fluxo de autorização
      await api.post("/admin/convenios/authorizations", {
        professional_id: form.professional_id || "",
        patient_name: form.patient_name,
        insurer_id: form.insurer_id,
        plan_id: form.plan_id || null,
        booking_id: form.booking_id || null,
        authorization_number: form.authorization_number || null,
        authorization_date: form.authorization_date || null,
        procedure_authorized: form.procedure_authorized || null,
        procedure_codes: form.procedure_codes
          ? String(form.procedure_codes).split(",").map(s => s.trim()).filter(Boolean)
          : [],
        sessions_authorized: form.sessions_authorized != null && form.sessions_authorized !== ""
          ? Number(form.sessions_authorized) : null,
        valid_until: form.valid_until || null,
        document_url: form.document_url || null,
        status: form.status || "pending",
        notes: form.notes || null,
      });
      toast.success("Autorização registrada");
      setModal(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  // Iter 68.64 — patch específico pra registrar decisão + campos completos
  const patchAuth = async (row, patch) => {
    try {
      await api.patch(`/admin/convenios/authorizations/${row.id}`, patch);
      toast.success("Autorização atualizada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex items-center gap-1 border border-slate-200 rounded-full p-1 bg-white">
          {[["", "Todos"], ["pending", "Pendentes"], ["approved", "Aprovadas"], ["rejected", "Rejeitadas"], ["expired", "Expiradas"]].map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v)} className={`text-xs px-3 py-1 rounded-full ${statusFilter === v ? "bg-[#0B2B62] text-white" : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <button onClick={() => { setForm({ patient_name: "", insurer_id: insurers?.[0]?.id, status: "pending" }); setModal(true); }} className="text-xs px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-[#0B2B62] font-semibold rounded-md inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" />Nova autorização</button>
      </div>
      <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-4 py-3">Paciente</th><th className="text-left px-4 py-3">Operadora</th><th className="text-left px-4 py-3">Nº Autorização</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Ações</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500 text-xs">Nenhuma autorização.</td></tr>}
            {list.map(r => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3">{r.patient_name}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{insurers.find(i => i.id === r.insurer_id)?.name}</td>
                <td className="px-4 py-3 text-slate-600 text-xs font-mono">{r.authorization_number || "—"}</td>
                <td className="px-4 py-3"><span className={`${cls} ${AUTH_CLS[r.status]}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5 justify-end">
                    {r.status === "pending" && (<>
                      <button onClick={() => setStatus(r, "approved")} className="text-xs px-2.5 py-1 text-emerald-700 border border-emerald-300 rounded-md hover:bg-emerald-50 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Aprovar</button>
                      <button onClick={() => setStatus(r, "rejected")} className="text-xs px-2.5 py-1 text-red-600 border border-red-200 rounded-md hover:bg-red-50 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />Rejeitar</button>
                    </>)}
                    <button onClick={() => remove(r)} className="text-xs px-2.5 py-1 text-red-600 border border-red-200 rounded-md hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="auth-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl my-4">
            <div className="p-5 border-b flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Fluxo Convênio · Autorização de Procedimento</div>
                <div className="font-display font-bold text-lg text-[#0B2B62]">Registrar autorização</div>
              </div>
              <button onClick={() => setModal(false)}><XCircle className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Paciente *</span>
                  <input value={form.patient_name || ""} onChange={e => setForm(p => ({ ...p, patient_name: e.target.value }))}
                    data-testid="auth-patient-name"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Operadora *</span>
                  <select value={form.insurer_id || ""} onChange={e => setForm(p => ({ ...p, insurer_id: e.target.value }))}
                    data-testid="auth-insurer"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                    <option value="">—</option>
                    {insurers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Nº autorização</span>
                  <input value={form.authorization_number || ""} onChange={e => setForm(p => ({ ...p, authorization_number: e.target.value }))}
                    data-testid="auth-number"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Data da autorização</span>
                  <input type="date" value={(form.authorization_date || "").slice(0, 10)}
                    onChange={e => setForm(p => ({ ...p, authorization_date: e.target.value ? `${e.target.value}T00:00:00Z` : "" }))}
                    data-testid="auth-date"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Validade</span>
                  <input type="date" value={(form.valid_until || "").slice(0, 10)}
                    onChange={e => setForm(p => ({ ...p, valid_until: e.target.value ? `${e.target.value}T23:59:59Z` : "" }))}
                    data-testid="auth-valid-until"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Sessões autorizadas</span>
                  <input type="number" min={0} max={999} value={form.sessions_authorized ?? ""}
                    onChange={e => setForm(p => ({ ...p, sessions_authorized: e.target.value }))}
                    data-testid="auth-sessions"
                    placeholder="ex.: 10"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                </label>
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Procedimento autorizado</span>
                <input value={form.procedure_authorized || ""} onChange={e => setForm(p => ({ ...p, procedure_authorized: e.target.value }))}
                  data-testid="auth-procedure"
                  placeholder="ex.: Consulta psicológica"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Códigos TUSS/AMB (CSV)</span>
                <input value={form.procedure_codes || ""} onChange={e => setForm(p => ({ ...p, procedure_codes: e.target.value }))}
                  data-testid="auth-codes"
                  placeholder="50000462, 50000470"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" />
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">ID do profissional</span>
                  <input value={form.professional_id || ""} onChange={e => setForm(p => ({ ...p, professional_id: e.target.value }))}
                    data-testid="auth-professional-id"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Booking vinculado</span>
                  <input value={form.booking_id || ""} onChange={e => setForm(p => ({ ...p, booking_id: e.target.value }))}
                    data-testid="auth-booking-id"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" />
                </label>
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Documento (URL)</span>
                <input value={form.document_url || ""} onChange={e => setForm(p => ({ ...p, document_url: e.target.value }))}
                  data-testid="auth-document"
                  placeholder="https://..."
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Observações</span>
                <textarea rows={2} value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  data-testid="auth-notes"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Status</span>
                <select value={form.status || "pending"} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                  data-testid="auth-status"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                  <option value="pending">Pendente</option>
                  <option value="approved">Aprovada</option>
                  <option value="rejected">Rejeitada</option>
                  <option value="expired">Expirada</option>
                </select>
              </label>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
              <button onClick={save} data-testid="auth-save"
                className="px-4 py-2 text-sm bg-amber-500 hover:bg-amber-400 text-[#0B2B62] rounded-md font-bold">Registrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================== CLAIMS (Guias TISS)
const ClaimsTab = ({ insurers }) => {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    const params = filter ? { status: filter } : {};
    const { data } = await api.get("/admin/convenios/claims", { params });
    setList(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (r, status) => {
    try { await api.patch(`/admin/convenios/claims/${r.id}`, { status }); toast.success(status); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-3">
      <div className="inline-flex items-center gap-1 border border-slate-200 rounded-full p-1 bg-white">
        {[["", "Todas"], ["draft", "Rascunho"], ["submitted", "Enviadas"], ["under_review", "Em análise"], ["paid", "Pagas"], ["denied", "Glosadas"], ["appealed", "Recursadas"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`text-xs px-3 py-1 rounded-full ${filter === v ? "bg-[#0B2B62] text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-4 py-3">Guia TISS</th><th className="text-left px-4 py-3">Operadora</th><th className="text-left px-4 py-3">Valor</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Ações</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500 text-xs">Nenhuma guia cadastrada.</td></tr>}
            {list.map(r => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3 text-slate-700 text-xs font-mono">{r.tiss_guide_number || r.id.slice(0, 8)}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{insurers.find(i => i.id === r.insurer_id)?.name}</td>
                <td className="px-4 py-3 tabular-nums font-semibold">{brl(r.total_value)}</td>
                <td className="px-4 py-3"><span className={`${cls} ${CLAIM_CLS[r.status]}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5 justify-end">
                    {r.status === "draft" && <button onClick={() => setStatus(r, "submitted")} className="text-xs px-2.5 py-1 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-50 inline-flex items-center gap-1"><Send className="w-3.5 h-3.5" />Enviar</button>}
                    {r.status === "submitted" && <button onClick={() => setStatus(r, "under_review")} className="text-xs px-2.5 py-1 text-amber-700 border border-amber-200 rounded-md hover:bg-amber-50 inline-flex items-center gap-1"><ChevronsRight className="w-3.5 h-3.5" />Em análise</button>}
                    {r.status === "under_review" && (<><button onClick={() => setStatus(r, "paid")} className="text-xs px-2.5 py-1 text-emerald-700 border border-emerald-300 rounded-md hover:bg-emerald-50 inline-flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />Pagar</button></>)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500">
        <b>Nota:</b> Criação manual de guia liga-se a `booking_id` — nesta v1 admin insere via API. UI de criação inline chega na Fase 2.
      </div>
    </div>
  );
};

// ========================================== BATCHES
const BatchesTab = ({ insurers }) => {
  const [list, setList] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/convenios/batches");
    setList(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (r, status) => {
    try { await api.patch(`/admin/convenios/batches/${r.id}`, { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-3">
      <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-4 py-3">Lote</th><th className="text-left px-4 py-3">Operadora</th><th className="text-left px-4 py-3">Guias</th><th className="text-left px-4 py-3">Total</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Ações</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500 text-xs">Nenhum lote cadastrado.</td></tr>}
            {list.map(r => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3 text-slate-700 text-xs font-mono">{r.batch_number || r.id.slice(0, 8)}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{insurers.find(i => i.id === r.insurer_id)?.name}</td>
                <td className="px-4 py-3 tabular-nums">{(r.claim_ids || []).length}</td>
                <td className="px-4 py-3 tabular-nums font-semibold">{brl(r.total_value)}</td>
                <td className="px-4 py-3"><span className={`${cls} ${BATCH_CLS[r.status]}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5 justify-end">
                    {r.status === "open" && <button onClick={() => setStatus(r, "sent")} className="text-xs px-2.5 py-1 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-50 inline-flex items-center gap-1"><Send className="w-3.5 h-3.5" />Enviar</button>}
                    {r.status === "sent" && <button onClick={() => setStatus(r, "closed")} className="text-xs px-2.5 py-1 text-emerald-700 border border-emerald-300 rounded-md hover:bg-emerald-50 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Fechar</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========================================== DENIALS
const DenialsTab = () => {
  const [list, setList] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/convenios/denials");
    setList(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const appeal = async (r) => {
    const reason = window.prompt("Motivo do recurso:", "");
    if (!reason) return;
    try { await api.post(`/admin/convenios/denials/${r.id}/appeal`, { appeal_reason: reason }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const remove = async (r) => {
    if (!window.confirm("Excluir glosa?")) return;
    try { await api.delete(`/admin/convenios/denials/${r.id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-3">
      <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-4 py-3">Guia</th><th className="text-left px-4 py-3">Código</th><th className="text-left px-4 py-3">Motivo</th><th className="text-left px-4 py-3">Valor</th><th className="text-left px-4 py-3">Recurso</th><th className="text-right px-4 py-3">Ações</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500 text-xs">Nenhuma glosa registrada.</td></tr>}
            {list.map(r => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3 font-mono text-xs">{r.claim_id.slice(0, 8)}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.denial_code || "—"}</td>
                <td className="px-4 py-3 text-slate-700 text-xs italic">"{r.denial_reason}"</td>
                <td className="px-4 py-3 tabular-nums font-semibold text-red-700">{brl(r.denied_value)}</td>
                <td className="px-4 py-3">
                  {r.appealed
                    ? <span className={`${cls} bg-purple-100 text-purple-800`}>Recursada</span>
                    : <span className="text-slate-400 text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5 justify-end">
                    {!r.appealed && <button onClick={() => appeal(r)} className="text-xs px-2.5 py-1 text-purple-700 border border-purple-300 rounded-md hover:bg-purple-50 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Recorrer</button>}
                    <button onClick={() => remove(r)} className="text-xs px-2.5 py-1 text-red-600 border border-red-200 rounded-md hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========================================== REPORTS
const ReportsTab = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/convenios/reports/overview").then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="p-6 text-slate-500 text-sm">Carregando relatório…</div>;

  const KPI = ({ label, value, hint, color = "text-[#0B2B62]" }) => (
    <div className="p-4 border border-slate-200 bg-white rounded-lg">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`font-display font-bold text-2xl mt-1 tabular-nums ${color}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Operadoras ativas" value={data.insurers.active} hint={`${data.insurers.tele_enabled} liberadas p/ tele`} />
        <KPI label="Planos" value={data.plans.total} />
        <KPI label="Credenciamentos ativos" value={data.credentials.active} color="text-emerald-700" hint={`${data.credentials.pending} pendentes`} />
        <KPI label="Guias em análise" value={data.workflow.claims_open} color="text-amber-700" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Faturado (pago)" value={brl(data.financial.paid)} color="text-emerald-700" />
        <KPI label="Aguardando análise" value={brl(data.financial.under_review)} color="text-amber-700" />
        <KPI label="Glosado" value={brl(data.financial.denied)} color="text-red-700" />
        <KPI label="Rascunhos" value={brl(data.financial.draft)} color="text-slate-500" />
      </div>
      <div className="text-xs text-slate-500 flex items-center gap-2">
        <BarChart3 className="w-3.5 h-3.5" /> Snapshot gerado em {new Date(data.generated_at).toLocaleString("pt-BR")}
      </div>
    </div>
  );
};

// ========================================== EXPORT (5 tabs)
export { EligibilityTab, AuthorizationsTab, ClaimsTab, BatchesTab, DenialsTab, ReportsTab };
